import React, { useCallback, useState } from "react";
import { Button, Label, Modal, TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import { momentsApiClient } from "../../lib/apiClient";
import { useDropzone } from "react-dropzone";
import { UserAuth } from "../../lib/authContext";
import { MdClose } from "react-icons/md";

const CreateEventModal = ({
  modalIsOpen,
  closeModal,
}: {
  modalIsOpen: boolean;
  closeModal: () => void;
}) => {
  const { userData, getAllEvents } = UserAuth();

  const [loading, setLoading] = useState(false); // State to track loading
  const [files, setFiles] = useState<File[]>([]);
  const [formState, setFormState] = useState({
    event_name: "",
    event_description: "",
    start_date: "",
    end_date: "",
    cover_image: "",
  });

  const resetState = () => {
    setFormState({
      event_name: "",
      event_description: "",
      start_date: "",
      end_date: "",
      cover_image: "",
    });
    setFiles([]);
  };

  const validateForm = () => {
    const { event_name, start_date, end_date } = formState;

    if (!event_name) {
      toast.error("Event name is required");
      return false;
    }
    if (!start_date) {
      toast.error("Start date is required");
      return false;
    }
    if (!end_date) {
      toast.error("End date is required");
      return false;
    }
    if (files.length === 0) {
      toast.error("Cover image is required");
      return false;
    }
    // Check if the selected file is a GIF
    if (files[0].type === "image/gif") {
      toast.error("GIF files are not allowed");
      setFiles([]);
      return false;
    }
    return true;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 1) {
      toast.error("Only one image is allowed!");
      return;
    }
    setFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
  });

  // Function to remove a file from the array
  const removeFile = (fileToRemove: File) => {
    setFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileToRemove.name)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true); // Set loading to true when form is submitted

      const formData = new FormData();
      formData.append("event_name", formState.event_name);
      formData.append("event_description", formState.event_description);
      formData.append("start_date", formState.start_date);
      formData.append("end_date", formState.end_date);
      formData.append("cover_image", files[0]); // Cover image file
      formData.append("email", userData?.email || "");

      try {
        const response = await momentsApiClient.post("/events", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        if (response.status === 201) {
          // console.log("Event created:", response.data);
          getAllEvents(); // Fetch all events after creating a new one
          toast.success("Event created successfully!");
          resetState();
          closeModal();
        } else toast.error("Error creating event.");
      } catch (err) {
        console.error("Error creating event:", err);
        toast.error("Error creating event.");
      } finally {
        setLoading(false); // Set loading to false after request completes
      }
    }
  };

  return (
    <Modal
      show={modalIsOpen}
      onClose={() => {
        closeModal();
        resetState();
      }}
    >
      <Modal.Header>Create Event</Modal.Header>
      <Modal.Body className="p-5 md:p-10 bg-[#A18DA31A] opacity-100 z-10">
        <form className="flex flex-col items-center" onSubmit={handleSubmit}>
          <div className="flex flex-col items-center gap-2 mb-3 md:mb-6 w-full">
            <div className="mb-2 block">
              <Label htmlFor="cover" value="Upload cover image" />{" "}
              <span className="text-gold">*</span>
            </div>
            {files.length === 0 && (
              <div
                {...getRootProps({
                  className:
                    "dropzone flex flex-col items-center justify-center p-10 border-2 border-dashed border-green-300 rounded-lg cursor-pointer hover:border-blue-500 w-full",
                })}
              >
                <input {...getInputProps()} type="file" accept="image/*" />
                <p className="text-green-500 text-center">
                  {isDragActive
                    ? "Drop the files here ..."
                    : "Drag 'n' drop files here, or click to select files"}
                </p>
              </div>
            )}
            {files.length !== 0 && (
              <div>
                {files.map((file) => (
                  <div
                    key={file.name}
                    className="flex flex-col w-full relative"
                  >
                    {/* Image */}
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="h-auto w-40 rounded-md mb-1"
                    />

                    {/* 'X' Icon to remove file */}
                    <button
                      type="button"
                      onClick={() => removeFile(file)}
                      className="absolute top-1 right-1 text-white bg-black p-1 rounded-full opacity-75 hover:opacity-100 z-10"
                    >
                      <MdClose size={20} />
                    </button>

                    <p className="text-xs text-gray-500 w-44 text-center">
                      {file.name} - {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:gap-6 w-full">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="name" value="Event Name" />
                <span className="text-gold">*</span>
              </div>
              <TextInput
                id="name"
                type="text"
                value={formState.event_name}
                onChange={(e) =>
                  setFormState((prevState) => ({
                    ...prevState,
                    event_name: e.target.value,
                  }))
                }
                placeholder="Wedding"
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="event_description" value="Event Description" />
              </div>
              <TextInput
                id="event_description"
                type="text"
                value={formState.event_description}
                onChange={(e) =>
                  setFormState((prevState) => ({
                    ...prevState,
                    event_description: e.target.value,
                  }))
                }
                placeholder="Small Reception"
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="start_date" value="Start Date" />
                <span className="text-gold">*</span>
              </div>
              <TextInput
                id="start_date"
                type="date"
                placeholder="dd-mm-yyyy"
                value={formState.start_date}
                onChange={(e) =>
                  setFormState((prevState) => ({
                    ...prevState,
                    start_date: e.target.value,
                  }))
                }
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="end_date" value="End Date" />
                <span className="text-gold">*</span>
              </div>
              <TextInput
                id="end_date"
                type="date"
                placeholder="dd-mm-yyyy"
                value={formState.end_date}
                onChange={(e) =>
                  setFormState((prevState) => ({
                    ...prevState,
                    end_date: e.target.value,
                  }))
                }
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
          </div>

          <Button
            disabled={loading}
            className="relative bg-black hover:!bg-gray-800 focus:ring-0 mt-8 group"
            type="submit"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8v-8H4z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                Create
                <span className="ml-1 inline-block transition-transform duration-300 transform group-hover:translate-x-1">{`->`}</span>
              </>
            )}
          </Button>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateEventModal;
